// src/UsersTab.jsx
import React, { useState, useEffect, useMemo } from 'react';
import Barcode from 'react-barcode';
import QRCode from 'react-qr-code';
import api from '../services/api';

// ✏️ عدّلي هذين المتغيّرين حسب مشروعك
const LIB_NAME = 'المكتبة الشبابية المجانية للشباب';
const UNIV_LOGO_URL = new URL('/logo.png', window.location.origin).href;

// حارس عام لمنع فتح الطباعة أكثر من مرة في نفس اللحظة
let __printing_in_progress = false;

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    address: '',
    whatsappLink: '',
  });
  const [file, setFile] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);

  // بحث وفرز
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'date' | 'alpha'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      const data = res.data;
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('فشل في تحميل المستخدمين:', err);
      setUsers([]);
    }
  };

  const handleChange = (e) => {
    if (e.target.name === 'profilePic') {
      setFile(e.target.files[0]);
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (file) data.append('profilePic', file);

      if (editingUserId) {
        await api.put(`/users/${editingUserId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/users', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setForm({ name: '', phone: '', address: '', whatsappLink: '' });
      setFile(null);
      setEditingUserId(null);
      fetchUsers();
    } catch (err) {
      console.error('خطأ في حفظ المستخدم:', err);
      alert(err.response?.data?.error || err.message);
    }
  };

  const handleEdit = (user) => {
    setForm({
      name: user.name,
      phone: user.phone || '',
      address: user.address || '',
      whatsappLink: user.whatsappLink || '',
    });
    setFile(null);
    setEditingUserId(user._id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    try {
      await api.delete(`/users/${id}`);
      setUsers((prev) => prev.filter((u) => u._id !== id));
    } catch (err) {
      console.error('خطأ في الحذف:', err);
      alert('حدث خطأ أثناء حذف المستخدم');
    }
  };

  const downloadSVG = (id) => {
    const wrapper = document.getElementById(id);
    if (!wrapper) return;
    const svg =
      wrapper.tagName.toLowerCase() === 'svg' ? wrapper : wrapper.querySelector('svg');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${id}.svg`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  // استنتاج وقت الإنشاء من createdAt أو من ObjectId
  const getCreatedAt = (u) => {
    if (u.createdAt) return new Date(u.createdAt);
    try {
      const tsHex = String(u._id).substring(0, 8);
      const ts = parseInt(tsHex, 16) * 1000;
      return new Date(ts);
    } catch {
      return new Date(0);
    }
  };

  // 🖨️ طباعة بطاقة الـQR — باستخدام iframe فقط (بدون أي window.print داخل الصفحة المطبوعة)
  const printBarcodeCard = (user) => {
    if (__printing_in_progress) return; // حارس
    __printing_in_progress = true;

    // التقاط SVG الـQR
    the_qr:
    {
      const qrContainer = document.getElementById(`uqr-${user._id}`);
      if (!qrContainer) {
        __printing_in_progress = false;
        alert('لم يتم العثور على عنصر الـQR للطباعة.');
        return;
      }
      const qrSvg = qrContainer.querySelector('svg');
      if (!qrSvg) {
        __printing_in_progress = false;
        alert('الـQR غير جاهز بعد.');
        return;
      }

      // تأمين النصوص
      const escapeHtml = (str) =>
        String(str || '').replace(/[&<>"']/g, (m) => (
          { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]
        ));

      const safeName = escapeHtml(user.name);
      const safeLib  = escapeHtml(LIB_NAME);

      // صفحة الطباعة: لا يوجد فيها أي استدعاء print — سيتم النداء من الأب فقط
      const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<base href="${window.location.origin}">
<title>طباعة الـQR - ${safeName}</title>
<style>
  /* صفحة واحدة بدون هوامش لتجنّب صفحتين */
  @page { size: 90mm 120mm; margin: 0; }

  html,body { margin:0; padding:0; background:#fff; }
  body {
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Noto Naskh Arabic", "Tajawal", Arial, sans-serif;
    text-align:center;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .sheet {
    width:100%;
    display:flex; align-items:center; justify-content:center;
    padding:0; margin:0;
  }

  .card {
    width: 80mm;
    border: 2px solid #000; /* حدود سوداء واضحة */
    border-radius:10px;
    padding: 8mm 6mm;
    text-align:center; box-shadow:0 0 0 1px rgba(0,0,0,.03) inset;
    overflow:hidden;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .qr svg { width:46mm; height:46mm; }
  .uname {
    font-size:13px; font-weight:700; color:#111827; margin-top:6mm;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  }
  .lib {
    font-size:16px; font-weight:800; color:#111827; margin-top:3mm;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  }
  .logo { width:28mm; height:auto; object-fit:contain; margin:6mm auto 0; opacity:.95; }
</style>
</head>
<body>
  <div class="sheet">
    <div class="card">
      <div class="qr">${qrSvg.outerHTML}</div>
      <div class="uname" title="${safeName}">${safeName}</div>
      <div class="lib" title="${safeLib}">${safeLib}</div>
      <img src="${UNIV_LOGO_URL}" alt="شعار" class="logo"/>
    </div>
  </div>
</body>
</html>`;

      // نطبع عبر iframe فقط
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);

      iframe.onload = () => {
        try {
          const cw = iframe.contentWindow;
          if (!cw) throw new Error('no contentWindow');
          // نطبع مرة واحدة فقط من الأب
          cw.focus();
          cw.print();

          // بعد الطباعة نزيل iframe ونحرر الحارس
          cw.onafterprint = () => {
            setTimeout(() => {
              document.body.removeChild(iframe);
              __printing_in_progress = false;
            }, 50);
          };

          // بعض المتصفحات لا تطلق onafterprint دومًا
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
              __printing_in_progress = false;
            }
          }, 5000);
        } catch (e) {
          console.error('فشل الطباعة:', e);
          document.body.removeChild(iframe);
          __printing_in_progress = false;
        }
      };

      // نكتب الـHTML مباشرة باستخدام srcdoc (يسمح بتحميل /logo.png عبر <base>)
      iframe.srcdoc = html;
    }
  };

  // تجهيز قائمة العرض بعد البحث والفرز
  const displayUsers = useMemo(() => {
    const term = query.trim().toLowerCase();
    let list = Array.isArray(users) ? [...users] : [];

    if (term) {
      list = list.filter((u) => String(u.name || '').toLowerCase().includes(term));
    }

    list.sort((a, b) => {
      if (sortBy === 'alpha') {
        const an = String(a.name || '');
        const bn = String(b.name || '');
        const cmp = an.localeCompare(bn, 'ar', { sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      } else {
        const ad = getCreatedAt(a).getTime();
        const bd = getCreatedAt(b).getTime();
        const cmp = ad - bd;
        return sortDir === 'asc' ? cmp : -cmp;
      }
    });

    return list;
  }, [users, query, sortBy, sortDir]);

  const fmtDate = (d) =>
    new Intl.DateTimeFormat('ar', { year: 'numeric', month: 'short', day: 'numeric' }).format(d);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-6 space-y-6" dir="rtl">
      <h2 className="text-3xl font-extrabold text-gray-900 text-center">👥 إدارة المستخدمين</h2>

      {/* شريط البحث والفرز */}
      <div className="sticky top-2 z-10 bg-white/90 backdrop-blur p-4 rounded-2xl shadow border border-yellow-100">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
          <div className="flex-1">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="🔎 ابحث بالاسم..."
              className="w-full p-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-gray-700 font-medium">الفرز حسب:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="p-2 rounded-lg border border-gray-300"
            >
              <option value="alpha">الاسم (أبجدي)</option>
              <option value="date">تاريخ الإضافة</option>
            </select>
            <button
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              className="px-3 py-2 rounded-lg border bg-yellow-100 text-gray-900 hover:bg-yellow-200"
              title="عكس الاتجاه"
            >
              {sortDir === 'asc' ? '⬆️ تصاعدي' : '⬇️ تنازلي'}
            </button>
          </div>
        </div>
      </div>

      {/* نموذج إضافة / تعديل */}
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-6 rounded-2xl shadow border border-gray-100"
      >
        <input
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="👤 الاسم"
          required
          className="p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
        />
        <input
          name="phone"
          value={form.phone}
          onChange={handleChange}
          placeholder="📞 رقم الهاتف"
          className="p-3 border rounded-lg"
        />
        <input
          name="address"
          value={form.address}
          onChange={handleChange}
          placeholder="🏠 العنوان"
          className="p-3 border rounded-lg"
        />
        <input
          name="whatsappLink"
          value={form.whatsappLink}
          onChange={handleChange}
          placeholder="🔗 رابط واتساب"
          className="p-3 border rounded-lg"
        />
        {/* ✅ ليبل واضح لصورة المستخدم */}
        <div className="col-span-full">
          <label className="block mb-1 text-gray-700 font-medium">🖼️ صورة المستخدم:</label>
          <input
            type="file"
            name="profilePic"
            accept="image/*"
            onChange={handleChange}
            className="p-3 border rounded-lg w-full"
          />
        </div>
        <button
          type="submit"
          className="col-span-full py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-extrabولد rounded-xl transition"
        >
          {editingUserId ? '💾 حفظ التعديلات' : '➕ إضافة مستخدم'}
        </button>
      </form>

      {/* شبكة الكروت (عمودية / مرتبة) */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {displayUsers.map((u) => {
          const created = getCreatedAt(u);
          return (
            <div
              key={u._id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition p-5 flex flex-col gap-5 overflow-hidden"
            >
              {/* هيدر الكرت */}
              <div className="flex items-center gap-4">
                {u.profilePic ? (
                  <img
                    src={`/library-api${u.profilePic}`}
                    alt={u.name}
                    className="w-16 h-16 rounded-full object-cover border flex-shrink-0"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-yellow-100 grid place-items-center text-xl flex-shrink-0">👤</div>
                )}
                <div className="min-w-0">
                  <p className="font-extrabold text-gray-900 truncate" title={u.name}>{u.name}</p>
                  <p className="text-gray-500 text-sm truncate" title={u.phone || ''}>{u.phone || '—'}</p>
                  <p className="text-gray-400 text-xs mt-0.5">أُضيف: {fmtDate(created)}</p>
                </div>
              </div>

              {/* الأكواد */}
              {u.barcode && (
                <div className="flex items-start gap-4">
                  {/* باركود */}
                  <div id={`ubc-${u._id}`} className="flex-1 text-center overflow-hidden">
                    <Barcode
                      value={u.barcode}
                      width={1.7}
                      height={70}
                      marginTop={6}
                      marginBottom={6}
                      displayValue={true}
                      fontSize={12}
                      background="#ffffff"
                    />
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadSVG(`ubc-${u._id}`); }}
                        className="text-xs text-blue-700 hover:underline"
                      >
                        تحميل الباركود
                      </button>
                      <span className="text-gray-300 hidden sm:inline">|</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); printBarcodeCard(u); }}
                        className="text-xs text-yellow-700 hover:underline"
                      >
                        🖨️ طباعة الـQR
                      </button>
                    </div>
                  </div>

                  {/* QR */}
                  <div id={`uqr-${u._id}`} className="text-center flex-shrink-0">
                    <QRCode value={u.barcode} size={88} />
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadSVG(`uqr-${u._id}`); }}
                      className="block text-xs text-blue-700 mt-1 hover:underline"
                    >
                      تحميل QR
                    </button>
                  </div>
                </div>
              )}

              {/* أزرار الإجراءات */}
              <div className="flex gap-2 justify-between">
                <button
                  onClick={() => handleEdit(u)}
                  className="flex-1 text-sm font-bold text-gray-900 bg-yellow-300 hover:bg-yellow-400 px-3 py-2 rounded-lg transition"
                >
                  تعديل
                </button>
                <button
                  onClick={() => handleDelete(u._id)}
                  className="flex-1 text-sm font-bold text-white bg-red-600 hover:bg-red-700 px-3 py-2 rounded-lg transition"
                >
                  حذف
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
